import { useState, useEffect } from 'react';

export interface VoiceOption {
  id: string;
  name: string;
  description: string;
  audio_key: string;
  gender: string;
}

interface VoicesResponse {
  male: VoiceOption[];
  female: VoiceOption[];
}

interface UseVoicesReturn {
  voices: VoicesResponse | null;
  isLoading: boolean;
  error: Error | null;
  getVoicesByGender: (gender: string) => VoiceOption[];
  getVoiceAudioKey: (specific_voice_id?: string, voiceGender?: string) => string;
  getVoiceName: (specific_voice_id?: string, voiceGender?: string) => string;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage voice data from the backend API
 *
 * This replaces the hardcoded voiceData.ts file with dynamic data from the backend.
 * The hook provides helper functions that match the previous API for easy migration.
 */
export const useVoices = (): UseVoicesReturn => {
  const [voices, setVoices] = useState<VoicesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchVoices = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${backendUrl}/voices/`);

      if (!response.ok) {
        throw new Error(`Failed to fetch voices: ${response.statusText}`);
      }

      const data: VoicesResponse = await response.json();
      setVoices(data);

      // Cache in sessionStorage for offline/faster subsequent loads
      try {
        sessionStorage.setItem('voicethera_voices', JSON.stringify(data));
      } catch (e) {
        console.warn('Failed to cache voices in sessionStorage:', e);
      }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error fetching voices');
      setError(errorObj);

      // Try to load from cache if fetch fails
      try {
        const cached = sessionStorage.getItem('voicethera_voices');
        if (cached) {
          const cachedData = JSON.parse(cached);
          setVoices(cachedData);
          console.info('Using cached voices data due to fetch failure');
        }
      } catch (cacheErr) {
        console.error('Failed to load cached voices:', cacheErr);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVoices();
  }, []);

  /**
   * Get voices filtered by gender
   */
  const getVoicesByGender = (gender: string): VoiceOption[] => {
    if (!voices) return [];
    return gender === 'male' ? voices.male : voices.female;
  };

  /**
   * Get ElevenLabs audio key for a specific voice selection
   * Matches the previous getVoiceAudioKey API from voiceData.ts
   */
  const getVoiceAudioKey = (specific_voice_id?: string, voiceGender?: string): string => {
    if (!voices) {
      // Fallback to defaults if voices not loaded
      return voiceGender === 'male' ? 'NOpBlnGInO9m6vDvFkFC' : 'PB6BdkFkZLbI39GHdnbQ';
    }

    if (specific_voice_id) {
      // Search in both male and female voices
      const allVoices = [...voices.male, ...voices.female];
      const voice = allVoices.find(v => v.id === specific_voice_id);
      if (voice) {
        return voice.audio_key;
      }
      console.warn('⚠️ Specific voice not found, falling back to gender default:', specific_voice_id);
    }

    // Fallback to default voices based on gender
    const fallbackKey = voiceGender === 'male' ? 'NOpBlnGInO9m6vDvFkFC' : 'PB6BdkFkZLbI39GHdnbQ';
    return fallbackKey;
  };

  /**
   * Get voice name for display
   * Matches the previous getVoiceName API from voiceData.ts
   */
  const getVoiceName = (specific_voice_id?: string, voiceGender?: string): string => {
    if (!voices) {
      return voiceGender === 'male' ? 'Marcus' : 'Sophia';
    }

    if (specific_voice_id) {
      const allVoices = [...voices.male, ...voices.female];
      const voice = allVoices.find(v => v.id === specific_voice_id);
      if (voice) return voice.name;
    }

    return voiceGender === 'male' ? 'Marcus' : 'Sophia';
  };

  return {
    voices,
    isLoading,
    error,
    getVoicesByGender,
    getVoiceAudioKey,
    getVoiceName,
    refetch: fetchVoices,
  };
};
